import { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import {
  SettingsGroup,
  SettingsHr,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import {
  ARROW_ALIGNMENT_CENTRE,
  POSITION_BOTTOM,
  POSITION_LEFT,
  POSITION_TOP,
  POSITION_RIGHT,
  BORDER_STYLE_BOX,
  BORDER_STYLE_SPEECH,
  ARROW_ALIGNMENT_TOP,
  ARROW_ALIGNMENT_LEFT,
  ARROW_ALIGNMENT_BOTTOM,
  ARROW_ALIGNMENT_RIGHT,
  ArrowAlignment,
  BORDER_STYLE_THOUGHT,
  FLEX_ALIGN_START,
} from 'global/constants';
import ActivityBuilderInlineSvg from 'approot/shared/inline-svg/activity-builder/activity-builder-inline-svg';
import {
  ActivityBuilderDrawerContent,
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerWrapper,
} from 'approot/activity-builder/activity-builder-drawer';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import {
  DISCUSSION_RESPONSE_TYPES,
  DISCUSSION_TYPENAME,
  DiscussionProperties,
  SettingsProps,
} from './discussion.constants';
import { SmallStyled } from 'approot/shared/styles/typography.style';
import { grey10, grey7 } from 'approot/shared/styles/colours.style';
import {
  CheckableMulti,
  CheckableSingle,
} from 'approot/shared/styles/buttons/checkable-input';
import { RangeFieldSetting } from '../shared/range-field';
import SettingTextAligment from '../shared/settings-text-alignment';
import { Box } from 'approot/shared/design-system/box/box.style';
import { useBatchComponentUpdateContext } from '../shared/batch-component-update.context';
import type { GetActivitiesSectionsQuery } from '../../lesson-builder.graphql.generated';
import type { Pluck } from 'lib/gql/pluck';

const MAX_WIDTH = 100;
const WIDTH_FIELD = 'width';

type FormData = {
  [WIDTH_FIELD]: number;
};

// there shouldn't be any cases in production where the style object is null
// because when a component is added it gets these values via server/templates/activity-builder-component-templates.ts
// however now that the style object is nullable, we need some fallbacks just in case which have been copied over from the template file
const defaultStyleProperties: Pluck<
  Extract<
    Pluck<GetActivitiesSectionsQuery, 'getActivitiesSections.components'>,
    { __typename: 'ActivitiesDiscussionComponent' }
  >,
  'properties.style'
> = {
  __typename: 'ComponentStyle',
  arrowPosition: null,
  arrowAlignment: null,
  backgroundColour: true,
  baseColour: grey10,
  border: BORDER_STYLE_BOX,
  textAlignment: FLEX_ALIGN_START,
  width: 100,
  outerBorder: null,
  outerBaseColour: null,
  outerBackgroundColour: null,
};

const DiscussionSettings = ({
  sectionId,
  id,
  properties,
  onCloseChildDrawer,
}: SettingsProps) => {
  const { closeDrawer } = useActivityBuilderContext();

  const { updateComponentProperties } = useBatchComponentUpdateContext();

  const formMethods = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      [WIDTH_FIELD]:
        properties.style?.width === null ? MAX_WIDTH : properties.style?.width,
    },
  });
  const { watch } = formMethods;
  const widthValue = watch(WIDTH_FIELD);

  const handleChange = useCallback(
    (newProperties: DiscussionProperties) => {
      updateComponentProperties({
        input: {
          sectionId,
          componentId: id,
          componentData: {
            properties: newProperties,
          },
        },
        properties: newProperties,
        typename: DISCUSSION_TYPENAME,
      });
    },
    [updateComponentProperties, id, sectionId]
  );

  const currentType = properties.style?.border;
  const currentPosition = properties.style?.arrowPosition;
  const currentAlignment = properties.style?.arrowAlignment;
  const isChildDrawer = !!onCloseChildDrawer;

  return (
    <ActivityBuilderDrawerWrapper
      captureKeypresses={!isChildDrawer}
      onCloseByEscape={isChildDrawer ? onCloseChildDrawer : closeDrawer}
    >
      <ActivityBuilderDrawerHeader
        title="Class Discussion Question"
        isBack={isChildDrawer}
        onClose={isChildDrawer ? onCloseChildDrawer : closeDrawer}
      />
      <ActivityBuilderDrawerContent>
        <SettingsGroup as="fieldset" marginBottom={0}>
          <ScreenReaderOnly as="legend">
            Text width and alignment
          </ScreenReaderOnly>
          <FormProvider {...formMethods}>
            <RangeFieldSetting
              fieldName={WIDTH_FIELD}
              label="Width"
              max={MAX_WIDTH}
              min={0}
              postfix="%"
              value={widthValue}
              onChange={width =>
                handleChange({
                  ...properties,
                  style: {
                    ...(properties.style || defaultStyleProperties),
                    width,
                  },
                })
              }
            />
          </FormProvider>

          <SettingTextAligment
            alignment={properties.style?.textAlignment ?? FLEX_ALIGN_START}
            setAlignment={textAlignment => {
              handleChange({
                ...properties,
                style: {
                  ...(properties.style || defaultStyleProperties),
                  textAlignment,
                },
              });
            }}
          />
        </SettingsGroup>

        <SettingsHr />
        <SettingsGroup as="fieldset" marginBottom={0}>
          <SettingsLabel as="legend">Enabled Responses</SettingsLabel>
          <Box $marginBottom={8}>
            <CheckableMulti
              name="responseType"
              canDeselectAll={false}
              options={DISCUSSION_RESPONSE_TYPES}
              selected={properties.discussion.responseTypes}
              onSelect={values => {
                handleChange({
                  ...properties,
                  discussion: {
                    ...properties.discussion,
                    responseTypes: values,
                  },
                });
              }}
            />
          </Box>
          <SmallStyled colour={grey7}>
            Select at least one option. Please note responses are temporarily
            disabled for Class Discussions but will return in the future.
          </SmallStyled>
        </SettingsGroup>

        <SettingsHr />
        <SettingsGroup as="fieldset" marginBottom={0}>
          <ScreenReaderOnly as="legend">
            Discussion container styles
          </ScreenReaderOnly>
          <SettingsGroup>
            <SettingsLabel>Style</SettingsLabel>
            <CheckableSingle
              variant="pill"
              name="borders"
              selected={currentType || BORDER_STYLE_BOX}
              options={[
                {
                  label: 'Default',
                  value: BORDER_STYLE_BOX,
                  'aria-label': 'Set container to a plain box style',
                  onClick: () =>
                    handleChange({
                      ...properties,
                      style: {
                        ...(properties.style || defaultStyleProperties),
                        arrowAlignment: null,
                        arrowPosition: null,
                        border: BORDER_STYLE_BOX,
                      },
                    }),
                },
                {
                  label: 'Speech Bubble',
                  value: BORDER_STYLE_SPEECH,
                  'aria-label': 'Set container to a speech bubble style',
                  onClick: () =>
                    handleChange({
                      ...properties,
                      style: {
                        ...(properties.style || defaultStyleProperties),
                        arrowAlignment:
                          properties.style?.arrowAlignment ||
                          ARROW_ALIGNMENT_CENTRE,
                        arrowPosition:
                          properties.style?.arrowPosition || POSITION_BOTTOM,
                        border: BORDER_STYLE_SPEECH,
                      },
                    }),
                },
                {
                  label: 'Thought Bubble',
                  value: BORDER_STYLE_THOUGHT,
                  'aria-label': 'Set container to a thought bubble style',
                  onClick: () =>
                    handleChange({
                      ...properties,
                      style: {
                        ...(properties.style || defaultStyleProperties),
                        arrowAlignment:
                          properties.style?.arrowAlignment ||
                          ARROW_ALIGNMENT_CENTRE,
                        arrowPosition:
                          properties.style?.arrowPosition || POSITION_BOTTOM,
                        border: BORDER_STYLE_THOUGHT,
                      },
                    }),
                },
              ]}
            />
          </SettingsGroup>

          {(currentType === BORDER_STYLE_SPEECH ||
            currentType === BORDER_STYLE_THOUGHT) && (
            <>
              <SettingsGroup marginBottom={12}>
                <SettingsLabel>Tail Position</SettingsLabel>
                <CheckableSingle
                  variant="button"
                  isRound
                  name="arrowPosition"
                  options={[
                    {
                      label: (
                        <ActivityBuilderInlineSvg
                          id="icon_info_box_speech_bottom"
                          width={26}
                          height={26}
                        />
                      ),
                      value: POSITION_BOTTOM,
                      'aria-label':
                        'Set container box with the speech style to have a bottom arrow position',
                    },
                    {
                      label: (
                        <ActivityBuilderInlineSvg
                          id="icon_info_box_speech_top"
                          width={26}
                          height={26}
                        />
                      ),
                      value: POSITION_TOP,
                      'aria-label':
                        'Set container box with the speech style to have a top arrow position',
                    },
                    {
                      label: (
                        <ActivityBuilderInlineSvg
                          id="icon_info_box_speech_right"
                          width={26}
                          height={26}
                        />
                      ),
                      value: POSITION_RIGHT,
                      'aria-label':
                        'Set container box with the speech style to have a right arrow position',
                    },
                    {
                      label: (
                        <ActivityBuilderInlineSvg
                          id="icon_info_box_speech_left"
                          width={26}
                          height={26}
                        />
                      ),
                      value: POSITION_LEFT,
                      'aria-label':
                        'Set container box with the speech style to have a left arrow position',
                    },
                  ]}
                  selected={currentPosition || POSITION_TOP}
                  onSelect={selected => {
                    let alignment = currentAlignment;
                    if (
                      (selected === ARROW_ALIGNMENT_TOP ||
                        selected === ARROW_ALIGNMENT_BOTTOM) &&
                      (currentAlignment === ARROW_ALIGNMENT_TOP ||
                        currentAlignment === ARROW_ALIGNMENT_BOTTOM)
                    ) {
                      alignment = ARROW_ALIGNMENT_CENTRE;
                    }

                    if (
                      ((selected === ARROW_ALIGNMENT_LEFT ||
                        selected === ARROW_ALIGNMENT_RIGHT) &&
                        currentAlignment === ARROW_ALIGNMENT_LEFT) ||
                      currentAlignment === ARROW_ALIGNMENT_RIGHT
                    ) {
                      alignment = ARROW_ALIGNMENT_CENTRE;
                    }

                    handleChange({
                      ...properties,
                      style: {
                        ...(properties.style || defaultStyleProperties),
                        arrowAlignment: alignment || null,
                        arrowPosition: selected,
                      },
                    });
                  }}
                />
              </SettingsGroup>

              <SettingsGroup>
                <ScreenReaderOnly>
                  Choose an arrow alignment for the info box speech bubble style
                </ScreenReaderOnly>
                <CheckableSingle
                  name="arrowAlignment"
                  options={[
                    ...(currentPosition === POSITION_LEFT ||
                    currentPosition === POSITION_RIGHT
                      ? [
                          {
                            label: 'Top',
                            value: ARROW_ALIGNMENT_TOP,
                          },
                        ]
                      : [
                          {
                            label: 'Left',
                            value: ARROW_ALIGNMENT_LEFT,
                          },
                        ]),
                    {
                      label: 'Centred',
                      value: ARROW_ALIGNMENT_CENTRE,
                    },
                    ...(currentPosition === POSITION_LEFT ||
                    currentPosition === POSITION_RIGHT
                      ? [
                          {
                            label: 'Bottom',
                            value: ARROW_ALIGNMENT_BOTTOM,
                          },
                        ]
                      : [
                          {
                            label: 'Right',
                            value: ARROW_ALIGNMENT_RIGHT,
                          },
                        ]),
                  ]}
                  selected={currentAlignment || ARROW_ALIGNMENT_RIGHT}
                  onSelect={value => {
                    handleChange({
                      ...properties,
                      style: {
                        ...(properties.style || defaultStyleProperties),
                        arrowAlignment: value as ArrowAlignment,
                      },
                    });
                  }}
                />
              </SettingsGroup>
            </>
          )}
        </SettingsGroup>
      </ActivityBuilderDrawerContent>
    </ActivityBuilderDrawerWrapper>
  );
};

export default DiscussionSettings;
